import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useAlertFeedCardConfig, useMergedAlertFeedEvents } from './hooks';
import { useAlertsData } from '@/features/EventsListLayout/hooks';
import { useOpenInNewWindow } from '@/hooks';
import { AppRoutes } from '@/constants/routes';
import type { FeedEmptyStateProps } from './components';

interface AlertsFeedProps {
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

export function AlertsFeed({ titleContent, emptyState }: AlertsFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();
    const { openEvent } = useOpenInNewWindow();
    const cardConfig = useAlertFeedCardConfig();

    const { events, isLoading, isLoadingMore, hasMore, loadMore } =
        useAlertsData({ pageKey: 'alerts' });

    const { itemKey } = cardConfig;
    const feedEvents = useMergedAlertFeedEvents(events, itemKey);

    return (
        <GenericEventFeed
            {...cardConfig}
            containerTitle={t('sidebar.recentAlerts')}
            titleContent={titleContent}
            onViewAll={() => navigate(AppRoutes.ALERTS)}
            items={feedEvents}
            isLoading={isLoading}
            emptyState={emptyState}
            onNavigate={(alert, clickEvent) =>
                openEvent(alert.event.id, clickEvent, {
                    state: { feedContext: { type: 'alerts' } },
                })
            }
            quickViewContextType="event-feed"
            pagination={{ hasMore, isLoadingMore, onLoadMore: loadMore }}
        />
    );
}
